import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import { Card, CardContent } from "@/components/ui/Card";
import { useAuth } from "@/hooks/useAuth";
import { CheckCircle2, Mail, Send, ShieldAlert, Clock, AlertTriangle, Pencil, Users, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { ROSTER } from "@/data/roster";

interface SettingValue {
  value: string;
  updatedBy: string | null;
  updatedAt: string | null;
}
type SettingsResponse = Record<string, SettingValue>;

interface NotifLog {
  id: string;
  kind: string;
  recipient: string;
  sentAt: string;
  subject?: string;
  result?: string;
}

const FEATURES: { key: string; title: string; subtitle: string; icon: typeof Mail }[] = [
  {
    key: "quiet_hours_enabled",
    title: "Quiet hours",
    subtitle: "When on, no emails sent between 8 PM – 7 AM PT or on weekends (except Monday digest).",
    icon: Clock,
  },
];

function timeAgo(iso?: string | null) {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "just now";
  const m = Math.round(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

export function AdminSettingsPanel() {
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [testKind, setTestKind] = useState<"weekly_digest_test" | "friday_summary_test" | "event_nudges_test">("weekly_digest_test");

  const { data: settings, isLoading } = useQuery<SettingsResponse>({
    queryKey: ["admin-settings"],
    queryFn: async () => {
      const res = await fetch("/api/settings", { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    enabled: isAdmin,
  });

  const { data: log = [] } = useQuery<NotifLog[]>({
    queryKey: ["notifications-log"],
    queryFn: async () => {
      const res = await fetch("/api/notifications/log", { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: isAdmin,
    refetchInterval: 30_000,
  });

  if (!isAdmin) {
    return (
      <Card>
        <CardContent className="flex items-center gap-3 p-6 text-caption text-muted-foreground">
          <ShieldAlert size={16} /> Admin only.
        </CardContent>
      </Card>
    );
  }

  const toggle = async (key: string, next: boolean) => {
    console.log("[admin-settings] toggle", key, "→", next);
    // Optimistic update — works even when cache is empty by seeding the slot
    qc.setQueryData<SettingsResponse>(["admin-settings"], (cur) => {
      const base: SettingsResponse = cur ?? {};
      return {
        ...base,
        [key]: { ...(base[key] ?? { updatedBy: null, updatedAt: null }), value: next ? "true" : "false" },
      };
    });
    try {
      const res = await fetch(`/api/settings/${key}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ value: next ? "true" : "false" }),
      });
      const text = await res.text();
      let data: { error?: string; key?: string; value?: string } = {};
      try { data = JSON.parse(text); } catch { /* non-JSON */ }
      if (!res.ok) throw new Error(data.error ?? text ?? `HTTP ${res.status}`);
      console.log("[admin-settings] saved", data);
    } catch (err) {
      console.warn("[admin-settings] toggle failed:", err);
      alert(`Couldn't save toggle: ${err instanceof Error ? err.message : "unknown"}`);
    } finally {
      // Refetch — but allow the optimistic value to stick for a moment so the
      // user sees the flip even if the GET race-loses to a stale cache layer
      setTimeout(() => qc.invalidateQueries({ queryKey: ["admin-settings"] }), 600);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch("/api/notifications/test-digest", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email_type: testKind }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`);
      if (data.skipped) {
        setTestResult({ ok: true, msg: data.error || "Nothing to send (e.g. no events for you right now)." });
      } else {
        setTestResult({ ok: true, msg: `Sent — check your inbox.` });
      }
      qc.invalidateQueries({ queryKey: ["notifications-log"] });
    } catch (err) {
      setTestResult({ ok: false, msg: err instanceof Error ? err.message : "Failed" });
    } finally {
      setTesting(false);
    }
  };

  const lastDigest = settings?.last_weekly_digest_at?.value;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-h2 font-semibold">Admin Settings</h2>
        <p className="mt-1 text-caption text-muted-foreground">
          Email notification controls. Toggles are global — owners get emailed per their own opt-out preference (coming soon).
        </p>
      </div>

      <Card>
        <CardContent className="space-y-3 p-5">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-body font-medium text-foreground">Send me a test email</div>
              <div className="text-caption text-muted-foreground">Renders the chosen template with current data and fires it to your address only.</div>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={testKind}
                onChange={(e) => setTestKind(e.target.value as typeof testKind)}
                className="h-9 rounded-md border border-input bg-card px-2 text-caption focus:border-primary focus:outline-none"
              >
                <option value="weekly_digest_test">Weekly digest (your week)</option>
                <option value="friday_summary_test">Friday stakeholder summary</option>
                <option value="event_nudges_test">Event nudge (overdue/blocked/due-soon)</option>
              </select>
              <button
                type="button"
                onClick={sendTest}
                disabled={testing}
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-3 text-caption font-medium text-white hover:bg-primary/90 disabled:opacity-50"
              >
                <Send size={14} /> {testing ? "Sending…" : "Send test"}
              </button>
            </div>
          </div>
          {testResult && (
            <div className={cn(
              "flex items-center gap-2 rounded-md px-3 py-2 text-caption",
              testResult.ok ? "bg-emerald-50 text-emerald-700" : "bg-destructive/10 text-destructive"
            )}>
              {testResult.ok ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
              {testResult.msg}
            </div>
          )}
        </CardContent>
      </Card>

      {FEATURES.map((f) => {
        const s = settings?.[f.key];
        const enabled = s?.value === "true";
        const Icon = f.icon;
        return (
          <Card key={f.key}>
            <CardContent className="flex items-start justify-between gap-4 p-5">
              <div className="flex items-start gap-3">
                <div className={cn("mt-0.5 rounded-md p-1.5", enabled ? "bg-emerald-50 text-emerald-700" : "bg-muted text-muted-foreground")}>
                  <Icon size={16} />
                </div>
                <div>
                  <div className="text-body font-medium text-foreground">{f.title}</div>
                  <div className="mt-0.5 max-w-xl text-caption text-muted-foreground">{f.subtitle}</div>
                  {s?.updatedBy && (
                    <div className="mt-1.5 text-micro text-muted-foreground">
                      {enabled ? "Enabled" : "Disabled"} {timeAgo(s.updatedAt)} by {s.updatedBy}
                    </div>
                  )}
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={enabled}
                onClick={() => toggle(f.key, !enabled)}
                disabled={isLoading}
                className={cn(
                  "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
                  enabled ? "bg-primary" : "bg-muted"
                )}
              >
                <span className={cn(
                  "inline-block h-5 w-5 rounded-full bg-white shadow transition-transform",
                  enabled ? "translate-x-5" : "translate-x-0.5"
                )} />
              </button>
            </CardContent>
          </Card>
        );
      })}

      <EmailKindCards />

      <ManualSend />

      <NotificationRules />

      <Card>
        <CardContent className="space-y-3 p-5">
          <div className="flex items-center justify-between">
            <div className="text-body font-medium text-foreground">Recent sends</div>
            <div className="text-micro text-muted-foreground">
              Last weekly digest run: {lastDigest ? `${timeAgo(lastDigest)} (${new Date(lastDigest).toLocaleString()})` : "never"}
            </div>
          </div>
          {log.length === 0 ? (
            <div className="rounded-md border border-dashed border-border bg-muted/20 px-3 py-6 text-center text-caption text-muted-foreground">
              No emails sent yet.
            </div>
          ) : (
            <ul className="divide-y divide-border rounded-md border border-border">
              {log.slice(0, 20).map((n) => {
                const ok = (n.result ?? "").startsWith("ok");
                return (
                  <li key={n.id} className="flex items-center justify-between gap-3 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-caption text-foreground">{n.subject || n.kind}</div>
                      <div className="text-micro text-muted-foreground">
                        {n.recipient} · {n.kind} · {timeAgo(n.sentAt)}
                      </div>
                    </div>
                    <span className={cn(
                      "shrink-0 rounded-full px-2 py-0.5 text-micro font-medium",
                      ok ? "bg-emerald-50 text-emerald-700" : "bg-destructive/10 text-destructive"
                    )}>
                      {ok ? "sent" : "error"}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ─── Top-level kind cards (at-a-glance enable/disable, drives the rule) ─── */

const KIND_CARDS: { kind: string; title: string; subtitle: string; icon: typeof Mail }[] = [
  {
    kind: "weekly_digest",
    title: "Weekly digest",
    subtitle: "Per-owner — every owner with open work gets a personalized summary of their week.",
    icon: Mail,
  },
  {
    kind: "friday_summary",
    title: "Friday stakeholder summary",
    subtitle: "Once-a-week roll-up — wins this week, top risks, and what's coming next 7 days.",
    icon: Send,
  },
  {
    kind: "event_nudges",
    title: "Event nudges",
    subtitle: "Per-event triggers — overdue, blocked, and due-in-2-days. Sent only when there are events. Capped at 8 per email.",
    icon: AlertTriangle,
  },
];

function EmailKindCards() {
  const qc = useQueryClient();
  const { data: rules, isLoading } = useQuery<NotifRules>({
    queryKey: ["notif-rules"],
    queryFn: async () => {
      const res = await fetch("/api/notifications/rules", { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
  });

  const toggle = async (kind: string, next: boolean) => {
    const cur = rules?.[kind]?.rule;
    if (!cur) return;
    qc.setQueryData<NotifRules>(["notif-rules"], (s) => {
      if (!s) return s;
      return { ...s, [kind]: { ...s[kind], rule: { ...s[kind].rule, enabled: next } } };
    });
    try {
      const res = await fetch(`/api/notifications/rules/${kind}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...cur, enabled: next }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? `HTTP ${res.status}`);
    } catch (err) {
      alert(`Couldn't save: ${err instanceof Error ? err.message : "unknown"}`);
    } finally {
      setTimeout(() => qc.invalidateQueries({ queryKey: ["notif-rules"] }), 500);
    }
  };

  return (
    <>
      {KIND_CARDS.map((c) => {
        const entry = rules?.[c.kind];
        const enabled = entry?.rule.enabled === true;
        const Icon = c.icon;
        return (
          <Card key={c.kind}>
            <CardContent className="flex items-start justify-between gap-4 p-5">
              <div className="flex items-start gap-3">
                <div className={cn("mt-0.5 rounded-md p-1.5", enabled ? "bg-emerald-50 text-emerald-700" : "bg-muted text-muted-foreground")}>
                  <Icon size={16} />
                </div>
                <div>
                  <div className="text-body font-medium text-foreground">{c.title}</div>
                  <div className="mt-0.5 max-w-xl text-caption text-muted-foreground">{c.subtitle}</div>
                  {entry && (
                    <div className="mt-1.5 text-micro text-muted-foreground">
                      {dayLabel(entry.rule.day)} · {entry.rule.time_pt} PT · cap {entry.rule.per_recipient_daily_cap}/day
                      {entry.last_fired_at ? ` · last fired ${timeAgoShort(entry.last_fired_at)}` : ""}
                    </div>
                  )}
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={enabled}
                onClick={() => toggle(c.kind, !enabled)}
                disabled={isLoading || !entry}
                className={cn(
                  "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
                  enabled ? "bg-primary" : "bg-muted"
                )}
              >
                <span className={cn(
                  "inline-block h-5 w-5 rounded-full bg-white shadow transition-transform",
                  enabled ? "translate-x-5" : "translate-x-0.5"
                )} />
              </button>
            </CardContent>
          </Card>
        );
      })}
    </>
  );
}

/* ─── Manual one-off digest send (admin picks recipient + CC + owner) ────── */

function ManualSend() {
  const qc = useQueryClient();
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [owner, setOwner] = useState("");
  const [subject, setSubject] = useState("");
  const [emailType, setEmailType] = useState<"weekly_digest" | "friday_summary" | "event_nudges">("weekly_digest");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const send = async () => {
    if (!to.trim()) return;
    setSending(true);
    setResult(null);
    try {
      const res = await fetch("/api/notifications/send-manual", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          to: to.trim(),
          cc: cc.split(",").map((s) => s.trim()).filter(Boolean),
          owner: owner.trim() || undefined,
          subject: subject.trim() || undefined,
          email_type: emailType,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`);
      setResult({ ok: true, msg: `Sent to ${data.to}${data.cc?.length ? ` (cc: ${data.cc.join(", ")})` : ""} · ${data.message_id}` });
      qc.invalidateQueries({ queryKey: ["notifications-log"] });
    } catch (err) {
      setResult({ ok: false, msg: err instanceof Error ? err.message : "Failed" });
    } finally {
      setSending(false);
    }
  };

  return (
    <Card>
      <CardContent className="space-y-3 p-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-body font-medium text-foreground">Send a manual email</div>
            <div className="text-caption text-muted-foreground">
              Pick a template, pick recipients (incl. CC), send. The Owner field controls whose tasks get rendered (only meaningful for the weekly digest / event nudges).
            </div>
          </div>
          <select
            value={emailType}
            onChange={(e) => setEmailType(e.target.value as typeof emailType)}
            className="h-9 rounded-md border border-input bg-card px-2 text-caption focus:border-primary focus:outline-none"
          >
            <option value="weekly_digest">Weekly digest</option>
            <option value="friday_summary">Friday stakeholder summary</option>
            <option value="event_nudges">Event nudge</option>
          </select>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-caption font-semibold text-muted-foreground">To <span className="text-destructive">*</span></label>
            <input
              list="manual-roster"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="someone@fello.ai"
              className="w-full rounded-md border border-input bg-card px-3 py-2 text-body focus:border-primary focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-caption font-semibold text-muted-foreground">CC <span className="text-muted-foreground">(comma-separated)</span></label>
            <input
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="stephen@fello.ai, nikhil@fello.ai"
              className="w-full rounded-md border border-input bg-card px-3 py-2 text-body focus:border-primary focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-caption font-semibold text-muted-foreground">Owner to render <span className="text-muted-foreground">(optional)</span></label>
            <input
              list="manual-roster-names"
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              placeholder="e.g. Lisa — defaults to recipient if in roster"
              className="w-full rounded-md border border-input bg-card px-3 py-2 text-body focus:border-primary focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-caption font-semibold text-muted-foreground">Subject <span className="text-muted-foreground">(optional)</span></label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Felix Launch — Lisa's summary · …"
              className="w-full rounded-md border border-input bg-card px-3 py-2 text-body focus:border-primary focus:outline-none"
            />
          </div>
        </div>
        <datalist id="manual-roster">
          {ROSTER.map((p) => <option key={p.email} value={p.email}>{p.name}</option>)}
        </datalist>
        <datalist id="manual-roster-names">
          {ROSTER.map((p) => <option key={p.email} value={p.firstName}>{p.name}</option>)}
        </datalist>
        <div className="flex items-center justify-between">
          <div className="text-micro text-muted-foreground">From <span className="font-mono">innovations@fello.ai</span> · sender's <code className="rounded bg-muted px-1">Reply-To</code> not set, replies go to the from address.</div>
          <button
            type="button"
            onClick={send}
            disabled={sending || !to.trim()}
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-3 text-caption font-medium text-white hover:bg-primary/90 disabled:opacity-50"
          >
            <Send size={14} /> {sending ? "Sending…" : "Send"}
          </button>
        </div>
        {result && (
          <div className={cn(
            "flex items-start gap-2 rounded-md px-3 py-2 text-caption",
            result.ok ? "bg-emerald-50 text-emerald-700" : "bg-destructive/10 text-destructive"
          )}>
            {result.ok ? <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> : <AlertTriangle size={14} className="mt-0.5 shrink-0" />}
            <span className="break-all">{result.msg}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ─── Per-rule notification config (recipients, timing, cap) ──────────────── */

type Recipients = "all_owners_with_open_work" | string[];
interface NotifRule {
  enabled: boolean;
  recipients: Recipients;
  day: string;
  time_pt: string;
  per_recipient_daily_cap: number;
}
interface NotifRuleEntry {
  label: string;
  rule: NotifRule;
  last_fired_at: string;
}
type NotifRules = Record<string, NotifRuleEntry>;

const DAYS = ["*", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function dayLabel(d: string) {
  if (d === "*") return "Every day";
  const long: Record<string, string> = { Mon: "Monday", Tue: "Tuesday", Wed: "Wednesday", Thu: "Thursday", Fri: "Friday", Sat: "Saturday", Sun: "Sunday" };
  return long[d] ?? d;
}

function recipientsLabel(r: Recipients): string {
  if (r === "all_owners_with_open_work") return "All owners with open work";
  if (Array.isArray(r)) return r.length === 0 ? "No one" : `${r.length} selected`;
  return "—";
}

function timeAgoShort(iso?: string) {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "just now";
  const m = Math.round(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

function NotificationRules() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<{ kind: string; rule: NotifRule } | null>(null);

  const { data: rules, isLoading } = useQuery<NotifRules>({
    queryKey: ["notif-rules"],
    queryFn: async () => {
      const res = await fetch("/api/notifications/rules", { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
  });

  const save = async (kind: string, rule: NotifRule) => {
    qc.setQueryData<NotifRules>(["notif-rules"], (cur) => {
      if (!cur) return cur;
      return { ...cur, [kind]: { ...cur[kind], rule } };
    });
    try {
      const res = await fetch(`/api/notifications/rules/${kind}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(rule),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error ?? `HTTP ${res.status}`);
      }
    } catch (err) {
      alert(`Save failed: ${err instanceof Error ? err.message : "unknown"}`);
    } finally {
      qc.invalidateQueries({ queryKey: ["notif-rules"] });
    }
  };

  return (
    <Card>
      <CardContent className="p-5">
        <div className="mb-3">
          <div className="text-body font-medium text-foreground">Notification rules</div>
          <div className="text-caption text-muted-foreground">Per-type recipient list, day/time, and per-recipient daily cap. Cron checks every 10 min and respects every rule independently.</div>
        </div>
        {isLoading || !rules ? (
          <div className="text-caption text-muted-foreground">Loading…</div>
        ) : (
          <div className="overflow-hidden rounded-md border border-border">
            <table className="w-full text-caption">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Notification</th>
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                  <th className="px-3 py-2 text-left font-medium">Recipients</th>
                  <th className="px-3 py-2 text-left font-medium">When</th>
                  <th className="px-3 py-2 text-left font-medium">Cap / day</th>
                  <th className="px-3 py-2 text-left font-medium">Last fired</th>
                  <th className="px-3 py-2 text-right font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(rules).map(([kind, entry]) => {
                  const r = entry.rule;
                  return (
                    <tr key={kind} className="border-t border-border">
                      <td className="px-3 py-2 font-medium text-foreground">{entry.label}</td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => save(kind, { ...r, enabled: !r.enabled })}
                          role="switch"
                          aria-checked={r.enabled}
                          className={cn("relative inline-flex h-5 w-9 items-center rounded-full transition-colors", r.enabled ? "bg-primary" : "bg-muted")}
                        >
                          <span className={cn("inline-block h-4 w-4 rounded-full bg-white shadow transition-transform", r.enabled ? "translate-x-5" : "translate-x-0.5")} />
                        </button>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{recipientsLabel(r.recipients)}</td>
                      <td className="px-3 py-2 text-muted-foreground">{dayLabel(r.day)} · {r.time_pt} PT</td>
                      <td className="px-3 py-2 text-muted-foreground">{r.per_recipient_daily_cap}</td>
                      <td className="px-3 py-2 text-muted-foreground">{timeAgoShort(entry.last_fired_at)}</td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => setEditing({ kind, rule: r })}
                          className="inline-flex h-7 items-center gap-1 rounded-md border border-input bg-card px-2 text-micro text-muted-foreground hover:text-foreground"
                        >
                          <Pencil size={11} /> Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {editing && (
          <RuleEditModal
            kind={editing.kind}
            label={rules?.[editing.kind]?.label ?? editing.kind}
            initial={editing.rule}
            onClose={() => setEditing(null)}
            onSave={async (next) => { await save(editing.kind, next); setEditing(null); }}
          />
        )}
      </CardContent>
    </Card>
  );
}

function RuleEditModal({ kind, label, initial, onClose, onSave }: {
  kind: string;
  label: string;
  initial: NotifRule;
  onClose: () => void;
  onSave: (r: NotifRule) => Promise<void>;
}) {
  const [rule, setRule] = useState<NotifRule>(initial);
  const [saving, setSaving] = useState(false);
  const isAll = rule.recipients === "all_owners_with_open_work";
  const explicitList: string[] = Array.isArray(rule.recipients) ? rule.recipients : [];

  const allRosterEmails = useMemo(() => ROSTER.map((p) => ({ email: p.email, name: p.name })), []);

  const toggleEmail = (email: string) => {
    setRule((r) => {
      const cur: string[] = Array.isArray(r.recipients) ? r.recipients : [];
      return { ...r, recipients: cur.includes(email) ? cur.filter((e) => e !== email) : [...cur, email] };
    });
  };

  // ESC closes
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 backdrop-blur-sm px-4" onClick={onClose}>
      <div className="w-full max-w-xl overflow-hidden rounded-xl border border-border bg-card shadow-card-hover" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <div className="text-body font-semibold text-foreground">Edit: {label}</div>
            <div className="text-micro text-muted-foreground">Internal kind: <span className="font-mono">{kind}</span></div>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 text-muted-foreground hover:text-foreground"><X size={16} /></button>
        </div>
        <div className="space-y-4 p-5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-caption font-semibold text-muted-foreground">Day of week</label>
              <select value={rule.day} onChange={(e) => setRule((r) => ({ ...r, day: e.target.value }))}
                className="w-full rounded-md border border-input bg-card px-3 py-2 text-body focus:border-primary focus:outline-none">
                {DAYS.map((d) => <option key={d} value={d}>{dayLabel(d)}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-caption font-semibold text-muted-foreground">Time (PT)</label>
              <input type="time" value={rule.time_pt} onChange={(e) => setRule((r) => ({ ...r, time_pt: e.target.value }))}
                className="w-full rounded-md border border-input bg-card px-3 py-2 text-body focus:border-primary focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-caption font-semibold text-muted-foreground">Per-recipient daily cap</label>
            <input type="number" min={0} value={rule.per_recipient_daily_cap}
              onChange={(e) => setRule((r) => ({ ...r, per_recipient_daily_cap: Math.max(0, Number(e.target.value || 0)) }))}
              className="w-32 rounded-md border border-input bg-card px-3 py-2 text-body focus:border-primary focus:outline-none" />
            <div className="mt-1 text-micro text-muted-foreground">Skip the send if recipient already received this many emails (any kind) in the last 24h. 0 = no cap.</div>
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-caption font-semibold text-muted-foreground">Recipients</label>
              <div className="flex items-center gap-2">
                <button type="button"
                  onClick={() => setRule((r) => ({ ...r, recipients: "all_owners_with_open_work" }))}
                  className={cn("inline-flex h-7 items-center gap-1 rounded-md border px-2 text-micro", isAll ? "border-primary bg-primary/10 text-primary" : "border-input bg-card text-muted-foreground hover:text-foreground")}>
                  <Users size={11} /> All with open work
                </button>
                <button type="button"
                  onClick={() => setRule((r) => ({ ...r, recipients: Array.isArray(r.recipients) ? r.recipients : [] }))}
                  className={cn("inline-flex h-7 items-center gap-1 rounded-md border px-2 text-micro", !isAll ? "border-primary bg-primary/10 text-primary" : "border-input bg-card text-muted-foreground hover:text-foreground")}>
                  Pick specific
                </button>
              </div>
            </div>
            {!isAll && (
              <div className="max-h-48 overflow-y-auto rounded-md border border-border">
                {allRosterEmails.map((p) => {
                  const checked = explicitList.includes(p.email);
                  return (
                    <label key={p.email} className="flex cursor-pointer items-center gap-2 border-b border-border/40 px-3 py-1.5 last:border-b-0 hover:bg-muted/40">
                      <input type="checkbox" checked={checked} onChange={() => toggleEmail(p.email)} />
                      <span className="text-caption text-foreground">{p.name}</span>
                      <span className="ml-auto text-micro text-muted-foreground">{p.email}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <button type="button" onClick={onClose} className="rounded-md border border-border px-3 py-1.5 text-caption text-muted-foreground hover:text-foreground">Cancel</button>
          <button type="button" disabled={saving}
            onClick={async () => { setSaving(true); try { await onSave(rule); } finally { setSaving(false); } }}
            className="rounded-md bg-primary px-3 py-1.5 text-caption font-medium text-white hover:bg-primary/90 disabled:opacity-50">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
